/** Mock profile listings with map coordinates (dev/demo data). */
import { getCityMapCenter, getCountryMapCenter, normalizeCityName } from "./locationTaxonomy";

const CITY_SPREAD_BASE_DEG = 0.0025;
const CITY_SPREAD_STEP_DEG = 0.0035;
const GOLDEN_ANGLE = 2.399963;

function isGpsLocatedProfile(profile) {
  return profile?.locationSource === "gps";
}

function cityGroupKey(profile) {
  return `${profile?.country || ""}|${normalizeCityName(profile?.city || "")}`;
}

function resolveProfileCoordinates(profile, viewerCenter) {
  if (profile?.origin === "external" || profile?.liveLocationEnabled === false) {
    const lat = profile.lat ?? profile.latitude;
    const lng = profile.lng ?? profile.longitude;
    if (lat != null && lng != null) {
      return { lat: Number(lat), lng: Number(lng) };
    }
  }

  if (isGpsLocatedProfile(profile)) {
    const lat = profile.lat ?? profile.latitude;
    const lng = profile.lng ?? profile.longitude;
    if (lat != null && lng != null) {
      return { lat: Number(lat), lng: Number(lng) };
    }
  }

  const lat = profile.lat ?? profile.latitude;
  const lng = profile.lng ?? profile.longitude;
  if (lat != null && lng != null) {
    return { lat: Number(lat), lng: Number(lng) };
  }

  const cityBase = getCityMapCenter(profile.city, profile.country) ?? viewerCenter;
  return { lat: cityBase.lat, lng: cityBase.lng };
}

/** Fan out city-level profiles so same-city pins don't stack on one point. */
export function spreadProfilesInSameCity(profiles = [], viewerCenter = null) {
  const center = viewerCenter ?? getCountryMapCenter("Switzerland");
  const groups = new Map();

  profiles.forEach((profile, index) => {
    if (isGpsLocatedProfile(profile)) return;
    const key = cityGroupKey(profile);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ profile, index });
  });

  const next = profiles.map((profile) => {
    const coords = resolveProfileCoordinates(profile, center);
    const distanceKm = profile.distanceKm != null
      ? profile.distanceKm
      : Math.round(haversineKm(center, coords) * 10) / 10;

    return {
      ...profile,
      ...coords,
      distanceKm,
    };
  });

  groups.forEach((entries) => {
    if (entries.length <= 1) return;

    const sorted = [...entries].sort((a, b) => {
      const idA = String(a.profile.id ?? a.profile.name ?? "");
      const idB = String(b.profile.id ?? b.profile.name ?? "");
      return idA.localeCompare(idB);
    });

    const anchor = sorted[0].profile;
    const cityBase = getCityMapCenter(anchor.city, anchor.country)
      ?? resolveProfileCoordinates(anchor, center);

    sorted.forEach(({ index }, slot) => {
      const angle = slot * GOLDEN_ANGLE;
      const radius = CITY_SPREAD_BASE_DEG + slot * CITY_SPREAD_STEP_DEG;
      const lat = cityBase.lat + Math.sin(angle) * radius;
      const lng = cityBase.lng + Math.cos(angle) * radius * 1.15;

      next[index] = {
        ...next[index],
        lat,
        lng,
        distanceKm: Math.round(haversineKm(center, { lat, lng }) * 10) / 10,
      };
    });
  });

  return next;
}

const BASE_LISTINGS = [
  {
    id: "l1",
    image: "https://picsum.photos/seed/mia-zurich-1/900/1200",
    photos: [
      "https://picsum.photos/seed/mia-zurich-1/900/1200",
      "https://picsum.photos/seed/mia-zurich-2/900/1200",
      "https://picsum.photos/seed/mia-zurich-3/900/1200",
      "https://picsum.photos/seed/mia-zurich-4/900/1200",
    ],
    tag: "Premium",
    name: "Mia",
    age: 28,
    title: "Mia, 28",
    desc: "Warm, discreet, and easy to talk to. I love good wine, slow evenings, and making you feel completely at ease from the first hello.",
    country: "Switzerland",
    city: "Zürich",
    region: "Zürich",
    category: "Host",
    profileTypes: ["Female", "Escort"],
    services: ["Premium", "Girlfriend Experience", "Verified"],
    languages: [
      { name: "English", stars: 5 },
      { name: "German", stars: 5 },
      { name: "French", stars: 4 },
    ],
    serviceFor: ["Men", "Couples"],
    contact: {
      whatsapp: "+41791234567",
      phone: "+41797654321",
      webcamEnabled: false,
    },
    time: "2 minutes ago",
  },
  {
    id: "l2",
    image: "https://picsum.photos/seed/midnight-performer/400/520",
    tag: "Premium",
    name: "Lara",
    age: 25,
    verified: true,
    title: "Lara, 25",
    desc: "Playful energy with a calm vibe. Available for private dates in Basel and nearby—always respectful, always unhurried.",
    country: "Switzerland",
    city: "Basel",
    category: "Performer",
    profileTypes: ["Female", "Escort"],
    services: ["Premium", "Oral", "Outdoor"],
    languages: [
      { name: "German", stars: 5 },
      { name: "English", stars: 4 },
      { name: "French", stars: 3 },
    ],
    serviceFor: ["Men", "Women"],
    contact: {
      whatsapp: "+41617888999",
      phone: "",
      webcamEnabled: false,
    },
    time: "5 minutes ago",
  },
  {
    id: "l3",
    image: "https://picsum.photos/seed/weekend-availability/400/520",
    tag: "New",
    name: "Anna",
    age: 31,
    title: "Anna, 31",
    desc: "Berlin-based and weekend-friendly. Looking for genuine connection, great conversation, and chemistry that feels natural.",
    country: "Germany",
    city: "Berlin",
    region: "Berlin",
    category: "Availability",
    profileTypes: ["Female"],
    services: ["New", "Girlfriend Experience"],
    languages: [
      { name: "German", stars: 5 },
      { name: "English", stars: 5 },
      { name: "Spanish", stars: 3 },
    ],
    serviceFor: ["Men", "Women", "Couples"],
    contact: {
      whatsapp: "",
      phone: "+49301234567",
      webcamEnabled: false,
    },
    time: "10 minutes ago",
  },
  {
    id: "l4",
    image: "https://picsum.photos/seed/riviera-experience/400/520",
    tag: "Premium",
    name: "Camille",
    age: 27,
    title: "Camille, 27",
    desc: "Elegant, attentive, and open-minded. Based on the Riviera—happy to plan something relaxed or a little more adventurous.",
    country: "France",
    city: "Nice",
    category: "Trans",
    profileTypes: ["Trans", "Escort"],
    services: ["Premium", "Roleplay", "Verified"],
    languages: [
      { name: "French", stars: 5 },
      { name: "English", stars: 5 },
      { name: "Italian", stars: 4 },
    ],
    serviceFor: ["Men", "Women", "Couples"],
    contact: {
      whatsapp: "+33612345678",
      phone: "+33612345678",
      webcamEnabled: true,
    },
    time: "15 minutes ago",
  },
  {
    id: "l5",
    image: "https://picsum.photos/seed/virtual-host/400/520",
    tag: "Spotlight",
    name: "Marco",
    age: 29,
    title: "Marco, 29",
    desc: "Charming host for dinners, events, and private time. Fluent in Spanish and English, always punctual and easygoing.",
    country: "Spain",
    city: "Madrid",
    category: "Host",
    profileTypes: ["Male", "Webcam"],
    services: ["Webcam", "Premium"],
    languages: [
      { name: "Spanish", stars: 5 },
      { name: "English", stars: 5 },
      { name: "French", stars: 2 },
    ],
    serviceFor: ["Men", "Women"],
    contact: {
      whatsapp: "",
      phone: "",
      webcamEnabled: true,
    },
    time: "22 minutes ago",
  },
  {
    id: "l6",
    image: "https://picsum.photos/seed/canal-soiree/400/520",
    tag: "Premium",
    name: "Giulia",
    age: 26,
    verified: true,
    title: "Giulia, 26",
    desc: "Florence local with a taste for art, food, and long walks. I keep things classy, fun, and never rushed.",
    country: "Italy",
    city: "Florence",
    region: "Tuscany",
    category: "Host",
    profileTypes: ["Couple", "Escort"],
    services: ["Premium", "Outdoor"],
    languages: [
      { name: "Italian", stars: 5 },
      { name: "English", stars: 4 },
      { name: "German", stars: 3 },
    ],
    serviceFor: ["Men", "Women", "Couples"],
    contact: {
      whatsapp: "+39335111222",
      phone: "+39335111222",
      webcamEnabled: false,
    },
    time: "30 minutes ago",
  },
  {
    id: "l7",
    image: "https://picsum.photos/seed/l7/400/520",
    tag: "Featured",
    name: "Elena",
    age: 26,
    title: "Elena, 26",
    desc: "Soft-spoken and present. Geneva evenings are my specialty—massage, conversation, and a calm atmosphere you'll remember.",
    country: "Switzerland",
    city: "Geneva",
    region: "Geneva",
    category: "Performer",
    profileTypes: ["Female", "Massage"],
    services: ["Verified", "Girlfriend Experience", "Erotic Massage", "Body-to-Body"],
    languages: [
      { name: "French", stars: 5 },
      { name: "English", stars: 5 },
      { name: "German", stars: 4 },
    ],
    serviceFor: ["Men"],
    contact: {
      whatsapp: "",
      phone: "",
      webcamEnabled: false,
    },
    time: "1 hour ago",
  },
  {
    id: "l8",
    image: "https://picsum.photos/seed/l8/400/520",
    tag: "New",
    name: "Sofia",
    age: 24,
    title: "Sofia, 24",
    desc: "LA-based, travel-ready, and confident. I enjoy upscale nights out, private time in, and clients who know what they want.",
    country: "United States",
    city: "Los Angeles",
    region: "California",
    category: "Performer",
    profileTypes: ["Female", "BDSM"],
    services: ["Domination", "Bondage", "Fetish", "Premium"],
    languages: [
      { name: "English", stars: 5 },
      { name: "Spanish", stars: 5 },
    ],
    serviceFor: ["Men", "Women", "Couples"],
    contact: {
      whatsapp: "+13105551234",
      phone: "",
      webcamEnabled: true,
    },
    time: "2 hours ago",
  },
];

export function haversineKm(a, b) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const aVal = sinDLat * sinDLat + sinDLon * sinDLon * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
  return R * c;
}

/** Place each listing at its city on the map (cluster spread happens after merge). */
export function buildMapProfiles(center, listings = BASE_LISTINGS, browseCountry = "") {
  const viewerCenter = center ?? getCountryMapCenter("Switzerland");
  const source = browseCountry
    ? listings.filter((l) => l.country === browseCountry)
    : listings;

  return source.map((listing) => {
    const coords = resolveProfileCoordinates(listing, viewerCenter);
    const distanceKm = Math.round(haversineKm(viewerCenter, coords) * 10) / 10;

    return {
      ...listing,
      ...coords,
      distanceKm,
      name: listing.name ?? listing.title,
    };
  });
}

/** Map API discover cards onto the same shape the home map expects. */
export function mapDiscoverItemsToMapProfiles(items = [], viewerCenter) {
  const center = viewerCenter ?? getCountryMapCenter("Switzerland");

  return (items || []).map((item) => {
    const coords = resolveProfileCoordinates(item, center);
    const distanceKm = item.distanceKm != null
      ? item.distanceKm
      : Math.round(haversineKm(center, coords) * 10) / 10;

    const isExternal = item.origin === "external";

    return {
      ...item,
      ...coords,
      distanceKm,
      name: item.name ?? item.username ?? item.title,
      desc: item.desc ?? item.bio ?? "",
      image: item.image ?? item.photos?.[0] ?? "",
      origin: item.origin || (isExternal ? "external" : "native"),
      verified: isExternal ? false : item.verified,
      premium: isExternal ? false : item.premium,
      liveLocationEnabled: isExternal ? false : item.liveLocationEnabled,
    };
  });
}

/** Real profiles first, then demo listings (deduped by id), with same-city spread applied. */
export function mergeMapProfiles(liveProfiles = [], mockProfiles = [], viewerCenter = null) {
  const seen = new Set();
  const merged = [];
  const includeMocks = liveProfiles.length === 0;

  for (const profile of liveProfiles) {
    if (!profile?.id || seen.has(profile.id)) continue;
    seen.add(profile.id);
    merged.push(profile);
  }

  if (includeMocks) {
    for (const profile of mockProfiles) {
      if (!profile?.id || seen.has(profile.id)) continue;
      seen.add(profile.id);
      merged.push(profile);
    }
  }

  return spreadProfilesInSameCity(merged, viewerCenter);
}

export const MOCK_LISTINGS = BASE_LISTINGS;

export function getMockProfileById(profileId) {
  if (!profileId) return null;
  const listing = BASE_LISTINGS.find((item) => item.id === profileId);
  if (!listing) return null;
  return {
    ...listing,
    name: listing.name ?? listing.title,
  };
}

function matchesTagFilters(profile, { categories = [], services = [] } = {}) {
  const types = profile.profileTypes || [];
  const svc = [
    ...(profile.services || []),
    ...(profile.externalServiceLabels || []),
  ];
  const tag = profile.tag || "";

  if (categories.length && !categories.some((c) => types.includes(c) || profile.category === c)) {
    return false;
  }

  if (services.length) {
    const serviceMatch = services.some((s) => {
      if (svc.includes(s)) return true;
      if (s === "Premium" && (tag === "Premium" || tag === "Featured")) return true;
      if (s === "New" && tag === "New") return true;
      return false;
    });
    if (!serviceMatch) return false;
  }

  return true;
}

export function filterProfiles(profiles, profileFilters = {}, options = {}) {
  const {
    nearby = false,
    distanceKm = 15,
    cities: selectedCities = [],
    categories = [],
    services = [],
  } = profileFilters;
  const { skipTagFilters = false } = options;

  const legacyCity = profileFilters.city;
  const cities = selectedCities.length
    ? selectedCities
    : legacyCity
      ? [legacyCity]
      : [];
  const normalizedCityFilters = cities.map((city) => normalizeCityName(city));

  return profiles.filter((profile) => {
    if (nearby && profile.distanceKm != null && profile.distanceKm > distanceKm) {
      return false;
    }
    if (skipTagFilters) return true;
    if (
      normalizedCityFilters.length
      && !normalizedCityFilters.includes(normalizeCityName(profile.city))
    ) {
      return false;
    }
    if (!matchesTagFilters(profile, { categories, services })) return false;
    return true;
  });
}
